/**
 * ALDER SEARCH — Fractional & Interim Leadership Page
 * URL: /executive-search/fractional-interim-leadership
 */

import { useEffect } from "react";
import { Link } from "wouter";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { useFaqSchema } from "@/hooks/useFaqSchema";

const fractionalInterimFaqs = [
  {
    question: "What is fractional product leadership in a VC- and PE-backed business?",
    answer: "Fractional product leadership means engaging a senior product leader on a part-time or project basis rather than as a full-time hire. In VC- and PE-backed businesses, fractional leaders are typically used to fill a capability gap during a transition period, to provide specialist expertise for a specific value creation initiative, or to bridge between an outgoing leader and a permanent hire.",
  },
  {
    question: "When should a VC- and PE-backed company use an interim product leader rather than a permanent hire?",
    answer: "An interim product leader is appropriate when the business needs immediate leadership and cannot wait for a full retained search, when the role requirements are likely to change as the business evolves, or when the board wants to assess the function before committing to a permanent appointment. Interim leaders are also used to manage a specific transformation or integration project.",
  },
  {
    question: "How quickly can you place a fractional or interim product leader?",
    answer: "We can typically place a fractional or interim product leader within two to four weeks of brief sign-off, depending on the seniority of the role and the availability of candidates in our network. Speed is the primary advantage of interim placement over retained search.",
  },
  {
    question: "What is the difference between a fractional CPO and an interim appointment?",
    answer: "A fractional CPO works part-time across multiple businesses simultaneously, typically providing leadership for businesses that do not yet need or cannot support a full-time executive. An interim appointment is a full-time temporary role, usually engaged to cover a specific period such as a leadership transition or a product transformation programme.",
  },
  {
    question: "Can fractional or interim leaders transition into permanent roles?",
    answer: "Yes, and this is a common outcome in VC- and PE-backed businesses. An interim product leader who performs well during a transition period is often well-positioned to be considered for the permanent role. We manage this transition carefully to ensure it serves both the business and the individual's career objectives.",
  }
];


const steps = [
  {
    num: "01",
    title: "Rapid Brief",
    body: "We align on the mandate in a single call — the role, the duration, the specific deliverables, and the profile required. We don't spend three weeks writing a brief for an interim placement.",
  },
  {
    num: "02",
    title: "Immediate Network Activation",
    body: "We go straight to our network of available interim product leaders. These are operators we know personally — people who've held comparable roles in VC- and PE-backed businesses and are available now or within weeks.",
  },
  {
    num: "03",
    title: "Accelerated Assessment",
    body: "We assess for immediate fit. Can this person walk into a VC- and PE-backed business on day one, understand the sponsor dynamic, engage the management team, and start delivering? Product capability is assumed. Pace and VC and PE context-fit is what we test.",
  },
  {
    num: "04",
    title: "Rapid Shortlist",
    body: "We move from mandate to shortlist in days. You'll see two to three credible candidates quickly — each with a clear recommendation, not just a profile.",
  },
  {
    num: "05",
    title: "Placement & Fast Onboarding",
    body: "We manage contracting and the first-week onboarding. We stay close during the first month to confirm the interim is embedded and delivering against the agreed brief.",
  },
  {
    num: "06",
    title: "Permanent Transition Planning",
    body: "Where an interim leads to a permanent hire, we plan the transition from the start. We run that permanent search with the same rigour — and with no conflict of interest.",
  },
];

const outcomes = [
  "Interim product leaders placed and operational within two to three weeks of mandate.",
  "Product leaders who've operated in VC- and PE-backed businesses before — no time lost explaining the model.",
  "Clean transition to permanent when the time is right. No conflict of interest.",
];

export default function FractionalInterimPage() {
  useFaqSchema(fractionalInterimFaqs, "fractional-faq-jsonld");
  useEffect(() => {
    const prev = document.title;
    document.title = "Fractional & Interim Product Leadership Search for VC- and PE-Backed Companies | Alder Search";
    const meta = document.querySelector('meta[name="description"]');
    const prevContent = meta?.getAttribute("content") ?? "";
    meta?.setAttribute(
      "content",
      "VC- and PE-ready fractional and interim product leaders placed fast. CPOs and senior product practitioners who step in at pace. No onboarding curve. Operational from day one."
    );
    return () => {
      document.title = prev;
      meta?.setAttribute("content", prevContent);
    };
  }, []);

  const handleEngageClick = () => {
    const el = document.querySelector("#contact");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    } else {
      window.location.href = "/#contact";
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "oklch(0.97 0.006 80)" }}>
      <Navigation />

      {/* HERO */}
      <section
        className="relative pt-40 pb-24 overflow-hidden"
        style={{
          background: "linear-gradient(160deg, oklch(0.18 0.055 155) 0%, oklch(0.245 0.065 155) 100%)",
        }}
      >
        <div className="container relative z-10">
          <div className="mb-4 flex items-center gap-2" style={{ fontFamily: "'DM Mono', monospace", fontSize: "0.65rem", letterSpacing: "0.14em", textTransform: "uppercase" }}>
            <Link href="/" style={{ color: "oklch(0.55 0.02 155)", textDecoration: "none" }} onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.72 0.09 75)"; }} onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.55 0.02 155)"; }}>Home</Link>
            <span style={{ color: "oklch(0.38 0.07 155)" }}>›</span>
            <Link href="/executive-search" style={{ color: "oklch(0.55 0.02 155)", textDecoration: "none" }} onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.72 0.09 75)"; }} onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.55 0.02 155)"; }}>Executive Search</Link>
            <span style={{ color: "oklch(0.38 0.07 155)" }}>›</span>
            <span style={{ color: "oklch(0.62 0.085 75)" }}>Fractional &amp; Interim Leadership</span>
          </div>
          <h1
            className="mb-6"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontWeight: 700,
              fontSize: "clamp(2.2rem, 5vw, 3.8rem)",
              lineHeight: 1.1,
              color: "oklch(0.97 0.006 80)",
              maxWidth: "760px",
            }}
          >
            Product Leadership. Deployed Fast. VC- and PE-Ready from Day One.
          </h1>
          <p
            className="mb-10"
            style={{
              fontFamily: "'Source Serif 4', Georgia, serif",
              fontWeight: 300,
              fontSize: "clamp(1rem, 2vw, 1.15rem)",
              lineHeight: 1.75,
              color: "oklch(0.78 0.018 80)",
              maxWidth: "580px",
            }}
          >
            When a portfolio company needs product leadership immediately, we place leaders who step in at pace. No onboarding curve. No learning the VC and PE environment. Ready from the first board meeting.
          </p>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={handleEngageClick}
              className="px-8 py-3.5 transition-all duration-300"
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                fontSize: "0.75rem",
                background: "oklch(0.62 0.085 75)",
                color: "oklch(0.18 0.055 155)",
                border: "none",
                borderRadius: "2px",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.72 0.09 75)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.62 0.085 75)";
              }}
            >
              Engage Alder Search
            </button>
            <Link
              href="/executive-search"
              className="px-8 py-3.5 transition-all duration-300"
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                fontWeight: 500,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                fontSize: "0.75rem",
                background: "transparent",
                color: "oklch(0.88 0.012 80)",
                border: "1px solid oklch(0.45 0.07 155)",
                borderRadius: "2px",
                textDecoration: "none",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.borderColor = "oklch(0.62 0.085 75)";
                (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.62 0.085 75)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLAnchorElement).style.borderColor = "oklch(0.45 0.07 155)";
                (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.88 0.012 80)";
              }}
            >
              ← Back to Executive Search
            </Link>
          </div>
        </div>
      </section>

      {/* OUTCOMES STRIP */}
      <section
        className="py-14"
        style={{ background: "oklch(0.245 0.065 155)", borderBottom: "1px solid oklch(0.32 0.07 155)" }}
      >
        <div className="container">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {outcomes.map((text, i) => (
              <div key={i} className="flex gap-4 items-start">
                <div
                  className="mt-1 shrink-0"
                  style={{
                    width: "20px",
                    height: "20px",
                    borderRadius: "50%",
                    background: "oklch(0.62 0.085 75)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                    <path d="M1 4L3.5 6.5L9 1" stroke="oklch(0.18 0.055 155)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
                <p
                  style={{
                    fontFamily: "'Source Serif 4', Georgia, serif",
                    fontWeight: 300,
                    fontSize: "0.92rem",
                    lineHeight: 1.65,
                    color: "oklch(0.82 0.015 80)",
                  }}
                >
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS SECTION */}
      <section className="py-24" style={{ background: "oklch(0.97 0.006 80)" }}>
        <div className="container">
          <div
            className="mb-3"
            style={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "0.62rem",
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              color: "oklch(0.62 0.085 75)",
            }}
          >
            How This Search Works
          </div>
          <h2
            className="mb-5"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontWeight: 700,
              fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)",
              lineHeight: 1.15,
              color: "oklch(0.245 0.065 155)",
              maxWidth: "600px",
            }}
          >
            Interim Placement Built for VC & PE Pace.
          </h2>
          <p
            className="mb-16"
            style={{
              fontFamily: "'Source Serif 4', Georgia, serif",
              fontWeight: 300,
              fontSize: "1rem",
              lineHeight: 1.75,
              color: "oklch(0.42 0.03 155)",
              maxWidth: "560px",
            }}
          >
            Speed is the point. But speed without quality is just risk. Our interim process is compressed without cutting the steps that matter.
          </p>

          <div className="flex flex-col gap-0">
            {steps.map((step) => (
              <div
                key={step.num}
                className="grid grid-cols-1 md:grid-cols-[80px_1fr] gap-4 py-8"
                style={{ borderTop: "1px solid oklch(0.88 0.01 80)" }}
              >
                <div
                  style={{
                    fontFamily: "'DM Mono', monospace",
                    fontWeight: 400,
                    fontSize: "0.72rem",
                    letterSpacing: "0.1em",
                    color: "oklch(0.62 0.085 75)",
                    paddingTop: "3px",
                  }}
                >
                  {step.num}
                </div>
                <div>
                  <h3
                    className="mb-2"
                    style={{
                      fontFamily: "'Playfair Display', Georgia, serif",
                      fontWeight: 600,
                      fontSize: "1.05rem",
                      color: "oklch(0.245 0.065 155)",
                    }}
                  >
                    {step.title}
                  </h3>
                  <p
                    style={{
                      fontFamily: "'Source Serif 4', Georgia, serif",
                      fontWeight: 300,
                      fontSize: "0.92rem",
                      lineHeight: 1.75,
                      color: "oklch(0.42 0.03 155)",
                    }}
                  >
                    {step.body}
                  </p>
                </div>
              </div>
            ))}
            <div style={{ borderTop: "1px solid oklch(0.88 0.01 80)" }} />
          </div>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section className="py-20" style={{ background: "oklch(0.18 0.055 155)" }}>
        <div className="container">
          <div className="mx-auto" style={{ maxWidth: "680px", textAlign: "center" }}>
            <div
              className="mb-6"
              style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontStyle: "italic",
                fontSize: "clamp(1.15rem, 2.5vw, 1.55rem)",
                lineHeight: 1.6,
                color: "oklch(0.88 0.012 80)",
              }}
            >
              "A VC- and PE-backed business in leadership transition can't wait twelve weeks. We don't ask it to."
            </div>
            <div
              style={{
                fontFamily: "'DM Mono', monospace",
                fontSize: "0.65rem",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                color: "oklch(0.62 0.085 75)",
              }}
            >
              Ryan Ollerenshaw, Founding Partner, Alder Search
            </div>
          </div>
        </div>
      </section>

      {/* CTA SECTION */}
      <section id="contact" className="py-24" style={{ background: "oklch(0.245 0.065 155)" }}>
        <div className="container text-center">
          <h2
            className="mb-5"
            style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontWeight: 700,
              fontSize: "clamp(1.8rem, 3.5vw, 2.8rem)",
              lineHeight: 1.15,
              color: "oklch(0.97 0.006 80)",
            }}
          >
            Leadership Gap Now — or on the Horizon?
          </h2>
          <p
            className="mb-10 mx-auto"
            style={{
              fontFamily: "'Source Serif 4', Georgia, serif",
              fontWeight: 300,
              fontSize: "1rem",
              lineHeight: 1.75,
              color: "oklch(0.72 0.018 80)",
              maxWidth: "520px",
            }}
          >
            If you have an immediate leadership gap, or can see one coming, let's talk. We can move from conversation to candidate in days.
          </p>
          <button
            onClick={handleEngageClick}
            className="px-10 py-4 transition-all duration-300"
            style={{
              fontFamily: "'DM Sans', system-ui, sans-serif",
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              fontSize: "0.75rem",
              background: "oklch(0.62 0.085 75)",
              color: "oklch(0.18 0.055 155)",
              border: "none",
              borderRadius: "2px",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.72 0.09 75)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = "oklch(0.62 0.085 75)";
            }}
          >
            Engage Alder Search
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
